import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import Loading from '@/shared/components/Carregando';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import Links from '@/shared/constants/navegacao';
import { EnumerateDefensores } from '@/modules/people/defensor.service';
import { readForSelect } from '@/modules/people/person.service';
import { CategoriaDefensorEnum } from '@/modules/people/enums/categoria-defensor.enum';
import { ValidarPermissaoUsuario } from '@/modules/system/auth/application/validate-permissions';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { Tabelas } from '@/shared/constants/tables';
import { removerAcentos } from '@/shared/utils/textos';
import type { ReadConselhoSuperiorBienioDto } from '@/api.types';
import {
  conselhoSuperiorFormSchema,
  type ConselhoSuperiorFormDto,
} from './conselho-superior.schema';
import {
  createConselhoSuperior,
  getConselhoSuperiorAtual,
  getConselhoSuperiorPorBienio,
  listConselhoSuperiorBienios,
  updateConselhoSuperior,
} from './conselho-superior.service';
import { dtoToForm, formToPayload, rotuloBienio } from './conselho-superior.utils';

type OpcaoDefensor = { value: number; label: string; categoria?: string | null };
type OpcaoPessoa = { value: string; label: string };

const selectMenuPortal = {
  menuPortal: (base: object) => ({ ...base, zIndex: 9999 }),
};

export default function ConselhoSuperiorEditPage() {
  const podeEditar = ValidarPermissaoUsuario(RoleEnum.CONSELHO_SUPERIOR, PermissionEnum.UPDATE);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bienios, setBienios] = useState<ReadConselhoSuperiorBienioDto[]>([]);
  const [defensores, setDefensores] = useState<OpcaoDefensor[]>([]);
  const [pessoaBusca, setPessoaBusca] = useState('');
  const [pessoaOpcoes, setPessoaOpcoes] = useState<OpcaoPessoa[]>([]);
  const [pessoaLoading, setPessoaLoading] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [auditoria, setAuditoria] = useState<{
    editadoPor?: { username?: string } | null;
    editadoEm?: string | null;
  }>({});

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<ConselhoSuperiorFormDto>({
    resolver: zodResolver(conselhoSuperiorFormSchema) as Resolver<ConselhoSuperiorFormDto>,
    defaultValues: {
      id: null,
      anoInicio: 0,
      anoFim: 0,
      defensorPublicoGeralId: null,
      subdefensorInstitucionaisId: null,
      subdefensorAdministrativosId: null,
      corregedorOuvidorGeralId: null,
      primeiraCategoriaIds: [],
      segundaCategoriaIds: [],
      participantesVoz: [],
    },
  });

  const anoInicio = watch('anoInicio');
  const id = watch('id');
  const participantesVoz = watch('participantesVoz');
  const primeiraCategoriaIds = watch('primeiraCategoriaIds');
  const segundaCategoriaIds = watch('segundaCategoriaIds');

  const carregarDefensores = useCallback(async () => {
    const res = await EnumerateDefensores(0, 10000);
    setDefensores(
      res.objects
        .map((d) => ({
          value: d.id,
          label: d.pessoa?.nome || `Defensor #${d.id}`,
          categoria: (d as { categoria?: string | null }).categoria ?? null,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    );
  }, []);

  const carregarBienio = useCallback(
    async (ano: number) => {
      setLoading(true);
      try {
        const dto = await getConselhoSuperiorPorBienio(ano);
        reset(dtoToForm(dto));
        setAuditoria({ editadoPor: dto.editadoPor, editadoEm: dto.editadoEm });
      } catch (error) {
        toast.error(String(error));
      } finally {
        setLoading(false);
      }
    },
    [reset],
  );

  useEffect(() => {
    if (!podeEditar) return;
    (async () => {
      setLoading(true);
      try {
        await carregarDefensores();
        const [atual, listaBienios] = await Promise.all([
          getConselhoSuperiorAtual(),
          listConselhoSuperiorBienios(),
        ]);
        setBienios(listaBienios);
        reset(dtoToForm(atual));
        setAuditoria({ editadoPor: atual.editadoPor, editadoEm: atual.editadoEm });
      } catch (error) {
        toast.error(String(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [carregarDefensores, podeEditar, reset]);

  useEffect(() => {
    const q = pessoaBusca.trim();
    if (q.length < 2) {
      setPessoaOpcoes([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPessoaLoading(true);
      try {
        const [lista] = await readForSelect({
          filtro: removerAcentos(q),
          skip: 0,
          take: 20,
        });
        if (cancelled) return;
        const idsSelecionados = new Set(participantesVoz.map((p) => p.id));
        setPessoaOpcoes(
          lista
            .filter((p) => !idsSelecionados.has(String(p.id)))
            .map((p) => ({ value: String(p.id), label: p.nome })),
        );
      } catch {
        if (!cancelled) setPessoaOpcoes([]);
      } finally {
        if (!cancelled) setPessoaLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pessoaBusca, participantesVoz]);

  const opcoesPrimeira = useMemo(() => {
    const ids = new Set(primeiraCategoriaIds);
    return defensores.filter(
      (d) =>
        ids.has(d.value) ||
        !d.categoria ||
        d.categoria === CategoriaDefensorEnum.PRIMEIRA_CATEGORIA,
    );
  }, [defensores, primeiraCategoriaIds]);

  const opcoesSegunda = useMemo(() => {
    const ids = new Set(segundaCategoriaIds);
    return defensores.filter(
      (d) =>
        ids.has(d.value) ||
        !d.categoria ||
        d.categoria === CategoriaDefensorEnum.SEGUNDA_CATEGORIA,
    );
  }, [defensores, segundaCategoriaIds]);

  const onSubmit = async (form: ConselhoSuperiorFormDto) => {
    if (!podeEditar) return;
    setSaving(true);
    try {
      const payload = formToPayload(form);
      const saved = form.id
        ? await updateConselhoSuperior(form.id, payload)
        : await createConselhoSuperior(payload);
      reset(dtoToForm(saved));
      setAuditoria({ editadoPor: saved.editadoPor, editadoEm: saved.editadoEm });
      const lista = await listConselhoSuperiorBienios();
      setBienios(lista);
      toast.success('Conselho Superior salvo.');
    } catch (error) {
      toast.error(String(error));
    } finally {
      setSaving(false);
    }
  };

  if (!podeEditar) {
    return (
      <div>
        <p className="text-muted mb-2">Você não tem permissão para editar o Conselho Superior.</p>
        <Link to={Links.ConselhoSuperior_Index}>Voltar para visualização</Link>
      </div>
    );
  }

  if (loading) return <Loading />;

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h4 className="mb-0">Editar Conselho Superior</h4>
        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0 small text-muted">Biênio</Form.Label>
          <Form.Select
            style={{ width: 180 }}
            value={anoInicio || ''}
            disabled={saving}
            title="Selecione o biênio que deseja editar"
            onChange={async (e) => {
              const next = Number(e.target.value);
              if (!next || next === anoInicio) return;
              if (isDirty && !window.confirm('Há alterações não salvas. Deseja trocar de biênio?')) {
                return;
              }
              await carregarBienio(next);
            }}
          >
            {bienios.map((b) => (
              <option key={b.anoInicio} value={b.anoInicio}>
                {b.rotulo}
                {b.id ? '' : ' (novo)'}
              </option>
            ))}
          </Form.Select>
          {id ? (
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={() => setModalHistorico(true)}
            >
              Histórico
            </Button>
          ) : null}
          <Button type="button" variant="primary" size="sm" disabled={saving} onClick={handleSubmit(onSubmit)}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Link className="btn btn-sm btn-outline-secondary" to={Links.ConselhoSuperior_Index}>
            Voltar
          </Link>
        </div>
      </div>

      <p className="text-muted small mb-3">
        Editando o biênio <strong>{rotuloBienio(watch('anoInicio'), watch('anoFim'))}</strong>.
      </p>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={saving}>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label>Defensor Público Geral</Form.Label>
              <Controller
                name="defensorPublicoGeralId"
                control={control}
                render={({ field }) => (
                  <Select<OpcaoDefensor>
                    inputId="cs-dpg"
                    options={defensores}
                    isClearable
                    isSearchable
                    placeholder="Selecione..."
                    value={defensores.find((o) => o.value === field.value) ?? null}
                    onChange={(opt) => field.onChange(opt?.value ?? null)}
                    menuPortalTarget={document.body}
                    styles={selectMenuPortal}
                  />
                )}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Subdefensor Público-Geral para Assuntos Institucionais</Form.Label>
              <Controller
                name="subdefensorInstitucionaisId"
                control={control}
                render={({ field }) => (
                  <Select<OpcaoDefensor>
                    inputId="cs-sub-inst"
                    options={defensores}
                    isClearable
                    isSearchable
                    placeholder="Selecione..."
                    value={defensores.find((o) => o.value === field.value) ?? null}
                    onChange={(opt) => field.onChange(opt?.value ?? null)}
                    menuPortalTarget={document.body}
                    styles={selectMenuPortal}
                  />
                )}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Subdefensor Público-Geral para Assuntos Administrativos</Form.Label>
              <Controller
                name="subdefensorAdministrativosId"
                control={control}
                render={({ field }) => (
                  <Select<OpcaoDefensor>
                    inputId="cs-sub-adm"
                    options={defensores}
                    isClearable
                    isSearchable
                    placeholder="Selecione..."
                    value={defensores.find((o) => o.value === field.value) ?? null}
                    onChange={(opt) => field.onChange(opt?.value ?? null)}
                    menuPortalTarget={document.body}
                    styles={selectMenuPortal}
                  />
                )}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Corregedor-Geral/Ouvidor-Geral</Form.Label>
              <Controller
                name="corregedorOuvidorGeralId"
                control={control}
                render={({ field }) => (
                  <Select<OpcaoDefensor>
                    inputId="cs-corregedor"
                    options={defensores}
                    isClearable
                    isSearchable
                    placeholder="Selecione..."
                    value={defensores.find((o) => o.value === field.value) ?? null}
                    onChange={(opt) => field.onChange(opt?.value ?? null)}
                    menuPortalTarget={document.body}
                    styles={selectMenuPortal}
                  />
                )}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label>1ª Categoria</Form.Label>
              <Controller
                name="primeiraCategoriaIds"
                control={control}
                render={({ field }) => (
                  <Select<OpcaoDefensor, true>
                    inputId="cs-1cat"
                    isMulti
                    options={opcoesPrimeira}
                    isClearable
                    isSearchable
                    placeholder="Selecione defensores..."
                    value={opcoesPrimeira.filter((o) => field.value.includes(o.value))}
                    onChange={(opts) => field.onChange(opts.map((o) => o.value))}
                    menuPortalTarget={document.body}
                    styles={selectMenuPortal}
                  />
                )}
              />
            </Col>
            <Col md={6}>
              <Form.Label>2ª Categoria</Form.Label>
              <Controller
                name="segundaCategoriaIds"
                control={control}
                render={({ field }) => (
                  <Select<OpcaoDefensor, true>
                    inputId="cs-2cat"
                    isMulti
                    options={opcoesSegunda}
                    isClearable
                    isSearchable
                    placeholder="Selecione defensores..."
                    value={opcoesSegunda.filter((o) => field.value.includes(o.value))}
                    onChange={(opts) => field.onChange(opts.map((o) => o.value))}
                    menuPortalTarget={document.body}
                    styles={selectMenuPortal}
                  />
                )}
              />
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Participantes com direito a voz</Form.Label>
            <Select<OpcaoPessoa>
              inputId="cs-voz"
              options={pessoaOpcoes}
              isLoading={pessoaLoading}
              isClearable
              isSearchable
              placeholder="Buscar pessoa (nome, CPF ou e-mail)..."
              value={null}
              onInputChange={(value, meta) => {
                if (meta.action === 'input-change') setPessoaBusca(value);
                return value;
              }}
              onChange={(opt) => {
                if (!opt) return;
                if (participantesVoz.some((p) => p.id === opt.value)) return;
                setValue(
                  'participantesVoz',
                  [...participantesVoz, { id: opt.value, nome: opt.label }],
                  { shouldDirty: true },
                );
                setPessoaBusca('');
                setPessoaOpcoes([]);
              }}
              noOptionsMessage={() =>
                pessoaBusca.trim().length < 2
                  ? 'Digite ao menos 2 caracteres'
                  : 'Nenhuma pessoa encontrada'
              }
              menuPortalTarget={document.body}
              styles={selectMenuPortal}
            />
            {participantesVoz.length > 0 ? (
              <ul className="list-group mt-2">
                {participantesVoz.map((p) => (
                  <li
                    key={p.id}
                    className="list-group-item d-flex justify-content-between align-items-center py-2"
                  >
                    <span>{p.nome}</span>
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      onClick={() =>
                        setValue(
                          'participantesVoz',
                          participantesVoz.filter((x) => x.id !== p.id),
                          { shouldDirty: true },
                        )
                      }
                    >
                      Remover
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-muted small mt-2">Nenhum participante adicionado.</div>
            )}
          </Form.Group>
        </fieldset>
      </Form>

      {id ? (
        <EditadoPorRodape
          dados={{
            editadoPor: auditoria.editadoPor ?? undefined,
            editadoEm: auditoria.editadoEm ?? undefined,
          }}
        />
      ) : null}

      {modalHistorico && id ? (
        <ModalHistorico
          show={modalHistorico}
          handleHide={() => setModalHistorico(false)}
          tabela={Tabelas.conselho_superior}
          idRegistro={id}
        />
      ) : null}
    </>
  );
}
